/**
 * Helper functions for the application
 */

/**
 * Parses a JSON error response body (e.g. `{ success: false, error: { message, code } }`)
 * and returns a user-facing message, or `null` if none found.
 */
export function getMessageFromApiErrorBody(body: unknown): string | null {
  if (body == null) {
    return null;
  }

  if (typeof body === 'string') {
    const msg = body.trim();
    return msg ? msg : null;
  }

  if (typeof body !== 'object') {
    return null;
  }

  const d = body as Record<string, unknown>;

  if (
    d.error &&
    typeof d.error === 'object' &&
    d.error !== null &&
    'message' in d.error &&
    typeof (d.error as Record<string, unknown>).message === 'string'
  ) {
    const errorObj = d.error as Record<string, unknown>;
    const baseMsg = String(errorObj.message).trim();
    
    // If it's a validation error, append field summary
    if (errorObj.code === 'VALIDATION_ERROR') {
      const summary = getApiValidationFieldSummary(body);
      if (summary) return `${baseMsg}: ${summary}`;
    }
    
    if (baseMsg) return baseMsg;
  }

  if (typeof d.message === 'string') {
    const msg = d.message.trim();
    if (msg) return msg;
  }

  if (typeof d.error === 'string') {
    const msg = d.error.trim();
    if (msg) return msg;
  }

  if (
    d.data &&
    typeof d.data === 'object' &&
    d.data !== null &&
    'message' in d.data &&
    typeof (d.data as Record<string, unknown>).message === 'string'
  ) {
    const msg = String((d.data as Record<string, unknown>).message).trim();
    if (msg) return msg;
  }

  if (Array.isArray(d.errors) && d.errors.length > 0) {
    const first = d.errors[0];
    if (typeof first === 'string' && first.trim()) return first.trim();
    if (
      first &&
      typeof first === 'object' &&
      'message' in first &&
      typeof (first as Record<string, unknown>).message === 'string'
    ) {
      const msg = String((first as Record<string, unknown>).message).trim();
      if (msg) return msg;
    }
  }

  return null;
}

/**
 * Builds a short summary from `error.fields` (e.g. VALIDATION_ERROR payloads) for toasts / logs.
 */
export function getApiValidationFieldSummary(body: unknown): string | null {
  if (body == null || typeof body !== 'object') return null;
  const d = body as Record<string, unknown>;
  const err = d.error;
  if (!err || typeof err !== 'object') return null;
  const fields = (err as Record<string, unknown>).fields;
  if (!Array.isArray(fields) || fields.length === 0) return null;
  const parts = fields.map((f) => {
    if (!f || typeof f !== 'object') return null;
    const o = f as Record<string, unknown>;
    const field = typeof o.field === 'string' ? o.field : null;
    const reason = typeof o.reason === 'string' ? o.reason : null;
    if (field && reason) return `${field}: ${reason}`;
    if (field) return field;
    return null;
  });
  const flat = parts.filter((p): p is string => !!p);
  return flat.length ? flat.join(' · ') : null;
}

/**
 * Extracts a user-facing message from an RTK Query `unwrap()` rejection
 * (e.g. `{ status, data }` where `data` is the response body).
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const err = error as Record<string, unknown>;
  if ('data' in err && err.data !== undefined) {
    const data = err.data as Record<string, unknown>;

    // Handle VALIDATION_ERROR with field summary
    const errorObj = data.error as Record<string, unknown> | undefined;
    if (errorObj?.code === 'VALIDATION_ERROR') {
      const summary = getApiValidationFieldSummary(data);
      if (summary) return `Validation failed: ${summary}`;
    }

    const fromBody = getMessageFromApiErrorBody(err.data);
    if (fromBody) return fromBody;
  }

  if (typeof err.message === 'string') {
    const msg = err.message.trim();
    if (msg) return msg;
  }

  return fallback;
}

/**
 * Maps complex backend field paths to local form keys and sets errors in react-hook-form
 * e.g. "userData.gender" -> "gender"
 */
export function handleApiValidationErrors(
  error: unknown, 
  setError: (name: any, error: { type: string; message: string }) => void,
  mapping: Record<string, string> = {}
) {
  if (!error || typeof error !== 'object') return;
  
  const err = error as Record<string, unknown>;
  const data = err.data as Record<string, unknown> | undefined;
  if (!data || !data.error || typeof data.error !== 'object') return;
  
  const errorObj = data.error as Record<string, unknown>;
  const fields = errorObj.fields;
  
  if (Array.isArray(fields)) {
    fields.forEach((f) => {
      if (!f || typeof f !== 'object') return;
      const o = f as Record<string, unknown>;
      const backendField = String(o.field || '');
      const reason = String(o.reason || 'Invalid value');
      
      // 1. Try explicit mapping first
      if (mapping[backendField]) {
        setError(mapping[backendField], { type: 'manual', message: reason });
        return;
      }
      
      // 2. Try to split by dot and take the last part (common pattern for nested payloads)
      // e.g. "userData.gender" -> "gender"
      const parts = backendField.split('.');
      const lastPart = parts[parts.length - 1];
      
      if (lastPart) {
        setError(lastPart as any, { type: 'manual', message: reason });
      }
    });
  }
}

/**
 * Converts a relative URL to an absolute URL
 * Useful for public assets in Vite
 */
export function toAbsoluteUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  // In Vite, public assets are served from the root
  // Remove leading slash if present to avoid double slashes
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return cleanUrl;
}

